import React, { useEffect, useId, useState } from 'react';
import * as AlertDialog from '@radix-ui/react-alert-dialog';
import { AlertTriangle, Loader2 } from 'lucide-react';
import '@/components/primitives/primitives.css';

/**
 * ConfirmDialog for the highest-risk, irreversible actions (deleting a
 * tenant): the confirm button stays disabled until the admin types the exact
 * name. Same look and focus behaviour as primitives/ConfirmDialog.
 * onConfirm may return a promise: the dialog shows a pending state and stays
 * open if it rejects (the caller reports the error).
 */
export default function TypeToConfirmDialog({ open, onOpenChange, title, description, children, expected, confirmLabel, onConfirm }) {
  const [typed, setTyped] = useState('');
  const [pending, setPending] = useState(false);
  const inputId = useId();

  useEffect(() => {
    if (open) setTyped('');
  }, [open]);

  const matches = typed.trim() === String(expected || '').trim();

  const confirm = async (e) => {
    e.preventDefault();
    if (!matches || pending) return;
    setPending(true);
    try {
      await onConfirm?.();
      onOpenChange(false);
    } catch {
      /* stays open so the admin can retry or cancel */
    } finally {
      setPending(false);
    }
  };

  return (
    <AlertDialog.Root open={open} onOpenChange={(next) => !pending && onOpenChange(next)}>
      <AlertDialog.Portal>
        <AlertDialog.Overlay className="is-dialog-overlay" />
        <AlertDialog.Content className="is-dialog" data-testid="type-confirm-dialog">
          <div className="is-dialog-head">
            <span className="is-dialog-icon" aria-hidden="true">
              <AlertTriangle size={16} />
            </span>
            <div style={{ minWidth: 0, flex: 1 }}>
              <AlertDialog.Title className="is-dialog-title">{title}</AlertDialog.Title>
              <AlertDialog.Description className="is-dialog-body">{description}</AlertDialog.Description>
              {children && <div className="is-dialog-body">{children}</div>}
              <form onSubmit={confirm} className="adm-field" style={{ marginTop: 'var(--space-4)' }}>
                <label htmlFor={inputId} className="adm-label">
                  Type <strong className="adm-mono">{expected}</strong> to confirm
                </label>
                <input
                  id={inputId}
                  className="adm-input"
                  value={typed}
                  onChange={(e) => setTyped(e.target.value)}
                  autoComplete="off"
                  spellCheck={false}
                  data-testid="type-confirm-input"
                />
              </form>
            </div>
          </div>
          <div className="is-dialog-actions">
            <AlertDialog.Cancel className="is-btn is-btn--secondary" disabled={pending}>Cancel</AlertDialog.Cancel>
            <button
              type="button"
              className="is-btn is-btn--danger"
              onClick={confirm}
              disabled={!matches || pending}
              aria-busy={pending || undefined}
              data-testid="type-confirm-submit"
            >
              {pending && <Loader2 className="is-spinner" aria-hidden="true" />}
              {confirmLabel}
            </button>
          </div>
        </AlertDialog.Content>
      </AlertDialog.Portal>
    </AlertDialog.Root>
  );
}
